import React, { useEffect, useState } from 'react';
import {
  checkDomainAvailability,
  registerDomainForUser,
  mapDomainError
} from '../../services/domainService';
import { StatusPill } from './domainStatus';

const EXTENSIONS = ['fr', 'com'];
const DOMAIN_NAME_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const PAID_PLANS = ['pro', 'premium', 'business'];

const Label = ({ children }) => (
  <p className="text-xs text-base-content/60 uppercase tracking-wide">{children}</p>
);

const Value = ({ children, className = '' }) => (
  <p className={`text-base-content text-sm mt-0.5 ${className}`}>{children || '—'}</p>
);

/**
 * Modale de réservation d'un domaine POUR un client (mode assistance admin).
 * Workflow : recherche de disponibilité (.fr/.com) → confirmation → réservation
 * via onCall admin → route admin du deployment-service. Aucune action réelle
 * n'est déclenchée sans clic « Confirmer la réservation ».
 */
const RegisterDomainModal = ({
  isOpen,
  onClose,
  uid,
  client = {},
  hasActiveDomain = false,
  hasBlockedDomain = false,
  onRegistered
}) => {
  const [domainName, setDomainName] = useState('');
  const [extension, setExtension] = useState('fr');
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [searchedName, setSearchedName] = useState('');
  const [results, setResults] = useState(null);
  const [step, setStep] = useState('search'); // search | confirm | done
  const [selected, setSelected] = useState(null);
  const [autoRenew, setAutoRenew] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [finalData, setFinalData] = useState(null);
  const [runError, setRunError] = useState(null);
  const wasOpenRef = React.useRef(false);

  const resetAll = () => {
    setDomainName('');
    setExtension('fr');
    setIsChecking(false);
    setCheckError('');
    setSearchedName('');
    setResults(null);
    setStep('search');
    setSelected(null);
    setAutoRenew(true);
    setIsRegistering(false);
    setFinalData(null);
    setRunError(null);
  };

  // Réinitialise le formulaire à chaque ouverture de la modale
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) resetAll();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const clientName = [client.firstName, client.lastName].filter(Boolean).join(' ') || client.email || 'Client';
  const proActive = client.subscriptionStatus === 'active' && PAID_PLANS.includes(client.subscriptionPlan);

  let blockedMessage = null;
  if (hasActiveDomain) {
    blockedMessage = 'Ce client possède déjà un domaine actif. Un seul domaine par abonnement est autorisé.';
  } else if (hasBlockedDomain) {
    blockedMessage = "Un enregistrement précédent a échoué : le verrou anti-doublon (domaine « Échec réservation ») doit être supprimé par le support avant toute nouvelle tentative.";
  }

  const handleSearch = async (event) => {
    event.preventDefault();
    const raw = domainName.trim().toLowerCase().replace(/\.(fr|com)$/, '');
    if (!raw) {
      setCheckError('Saisissez un nom de domaine.');
      return;
    }
    if (!DOMAIN_NAME_PATTERN.test(raw)) {
      setCheckError('Caractères autorisés : lettres, chiffres et tirets (pas d\u2019extension).');
      return;
    }
    setIsChecking(true);
    setCheckError('');
    setSearchedName(raw);
    try {
      const data = await checkDomainAvailability(raw, uid);
      setResults(data?.domains || []);
      setStep('search');
    } catch (error) {
      const mapped = mapDomainError(error);
      setCheckError(mapped.message || 'Impossible de vérifier la disponibilité.');
      setResults(null);
    } finally {
      setIsChecking(false);
    }
  };

  const handleChoose = (result) => {
    setSelected(result);
    setRunError(null);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!selected || !searchedName) return;
    setIsRegistering(true);
    setRunError(null);
    try {
      const data = await registerDomainForUser(uid, searchedName, selected.extension, autoRenew);
      setFinalData(data || {});
      setStep('done');
      onRegistered && onRegistered(data);
    } catch (error) {
      const mapped = mapDomainError(error);
      setRunError(mapped);
      // On reste sur l'étape de confirmation pour afficher l'erreur
    } finally {
      setIsRegistering(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={isRegistering ? undefined : onClose} />
      <div className="relative bg-base-100 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* En-tête */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-base-200">
          <h3 className="font-bold text-lg text-base-content">
            Réserver un domaine pour {clientName}
          </h3>
          <button onClick={onClose} className="btn btn-sm btn-ghost btn-circle" aria-label="Fermer">✕</button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {blockedMessage && (
            <div className={`border rounded-lg p-3 text-sm text-base-content ${hasBlockedDomain ? 'bg-error/10 border-error/30' : 'bg-warning/10 border-warning/30'}`}>
              {hasBlockedDomain ? '⛔' : 'ℹ'} {blockedMessage}
            </div>
          )}
          {!proActive && !blockedMessage && (
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 text-sm text-base-content">
              ⚠ Ce client n'a pas d'abonnement Pro actif ({client.subscriptionPlan || 'free'} · {client.subscriptionStatus || 'inactive'}). La réservation sera refusée par le serveur.
            </div>
          )}

          {/* Recherche .fr/.com */}
          <form onSubmit={handleSearch} className="space-y-3">
            <div>
              <Label>Nom de domaine à rechercher</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                <input
                  type="text"
                  value={domainName}
                  onChange={(event) => setDomainName(event.target.value)}
                  placeholder="monsite"
                  className="input input-bordered flex-1 min-w-48"
                  autoComplete="off"
                />
                <div className="flex items-center gap-1 bg-base-200 rounded-lg p-1">
                  {EXTENSIONS.map((ext) => (
                    <button
                      key={ext}
                      type="button"
                      onClick={() => setExtension(ext)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        extension === ext ? 'bg-primary text-primary-content' : 'text-base-content/70 hover:text-base-content'
                      }`}
                    >
                      .{ext}
                    </button>
                  ))}
                </div>
                <button type="submit" className="btn btn-primary" disabled={isChecking}>
                  {isChecking ? <span className="loading loading-spinner loading-sm" /> : 'Rechercher'}
                </button>
              </div>
            </div>
          </form>

          {checkError && (
            <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-sm text-error">{checkError}</div>
          )}

          {/* Résultats de disponibilité */}
          {results && (
            <div>
              <p className="text-sm text-base-content/70 mb-2">
                Disponibilité de <span className="font-mono font-semibold text-base-content">{searchedName}</span>
              </p>
              <ul className="space-y-2">
                {results.map((result) => {
                  const busy = !!blockedMessage || result.available !== true;
                  return (
                    <li key={result.domain} className="border border-base-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono font-semibold text-base-content">{result.domain}</p>
                        {result.price?.amount != null ? (
                          <p className="text-xs text-base-content/60 mt-0.5">
                            {result.price.amount} {result.price.currency || 'EUR'} / an
                          </p>
                        ) : (
                          <p className="text-xs text-base-content/60 mt-0.5">Prix non communiqué</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {result.status === 'error' ? (
                          <StatusPill label="Erreur de vérification" status="failed" />
                        ) : result.available ? (
                          <StatusPill label="Disponible" status="active" />
                        ) : (
                          <StatusPill label="Indisponible" status="registration_failed" />
                        )}
                        <button
                          className="btn btn-sm btn-outline btn-success"
                          disabled={busy}
                          onClick={() => handleChoose(result)}
                          title={blockedMessage || (result.available ? 'Réserver ce domaine pour le client' : 'Domaine indisponible')}
                        >
                          Réserver
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {step === 'confirm' && selected && (
            <div className="border-t border-base-200 pt-4 space-y-4">
              <p className="text-sm text-base-content/70">
                Confirmez la réservation pour le compte de ce client. La réservation est immédiate et facturée au compte client.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-base-200/30 border border-base-200 rounded-lg p-3">
                  <Label>Client</Label>
                  <Value className="font-medium">{clientName}</Value>
                  <Value className="text-xs text-base-content/60">{client.email}</Value>
                  {client.isCompany && client.companyName && <Value className="text-xs">{client.companyName}</Value>}
                </div>
                <div className="bg-base-200/30 border border-base-200 rounded-lg p-3">
                  <Label>Domaine à réserver</Label>
                  <Value className="font-mono font-semibold">{searchedName}.{selected.extension}</Value>
                  <Value className="text-xs">
                    {selected.price?.amount != null
                      ? `${selected.price.amount} ${selected.price.currency || 'EUR'} / an`
                      : 'Prix non communiqué'}
                  </Value>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary checkbox-sm"
                  checked={autoRenew}
                  onChange={(event) => setAutoRenew(event.target.checked)}
                />
                <span className="text-sm text-base-content">Renouvellement automatique activé (reconduction annuelle)</span>
              </label>

              {runError && (
                <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-error">{runError.code}</p>
                  <p className="text-base-content">{runError.message}</p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button className="btn btn-outline" onClick={() => setStep('search')} disabled={isRegistering}>
                  Retour
                </button>
                <button
                  className={`btn btn-success ${isRegistering ? 'loading' : ''}`}
                  onClick={handleConfirm}
                  disabled={isRegistering}
                >
                  {isRegistering ? 'Réservation en cours…' : 'Confirmer la réservation'}
                </button>
              </div>
              {isRegistering && (
                <p className="text-xs text-base-content/60 text-right">
                  Enregistrement OpenProvider + configuration DNS en cours (peut prendre quelques secondes)…
                </p>
              )}
            </div>
          )}

          {step === 'done' && (
            <div className="border-t border-base-200 pt-4 space-y-4">
              {finalData?.success !== false ? (
                <div className="bg-success/10 border border-success/30 rounded-lg p-4">
                  <p className="text-success font-semibold">✅ Domaine réservé avec succès</p>
                  <p className="text-base-content mt-1">
                    <span className="font-mono font-semibold">
                      {finalData?.domain || `${searchedName}.${selected?.extension}`}
                    </span>{' '}
                    est maintenant réservé sur le compte du client.
                  </p>
                  {(finalData?.domainId || finalData?.trackingId) && (
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      {finalData?.domainId && (
                        <div><Label>ID OpenProvider</Label><Value className="text-xs font-mono">{finalData.domainId}</Value></div>
                      )}
                      {finalData?.trackingId && (
                        <div><Label>Suivi (tracking)</Label><Value className="text-xs font-mono break-all">{finalData.trackingId}</Value></div>
                      )}
                    </div>
                  )}
                  {finalData?.expirationDate && (
                    <p className="text-xs text-base-content/60 mt-2">
                      Expiration : {new Date(finalData.expirationDate).toLocaleDateString('fr-FR')}
                      {finalData.dnsConfigured ? ' · DNS configurés automatiquement' : ''}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-error/10 border border-error/30 rounded-lg p-4">
                  <p className="text-error font-semibold">⛔ Réservation en échec</p>
                  {runError && <p className="text-base-content mt-1">{runError.code} : {runError.message}</p>}
                  <p className="text-xs text-base-content/60 mt-1">
                    Consultez le détail du domaine (onglet Domaine) pour les étapes et le code d'erreur.
                  </p>
                </div>
              )}
              <div className="flex justify-end">
                <button className="btn btn-primary" onClick={onClose}>Fermer</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterDomainModal;
